import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchOrganizationUsers } from '../services/authService';
import './SearchableEmailDropdown.css';

const SearchableEmailDropdown = ({ organizationId, onSelect, onAddNew, existingEmails = [], placeholder = "Search for email..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchUsers = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const users = await searchOrganizationUsers(organizationId, term);
      const existing = new Set(existingEmails.map((email) => email.toLowerCase()));
      setResults(users.filter((user) => !existing.has(user.email.toLowerCase())));
    } catch (error) {
      console.error('Error searching users:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [existingEmails, organizationId]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm) {
        searchUsers(searchTerm);
        setShowDropdown(true);
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchUsers]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleSelect = (user) => {
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
    onSelect(user);
  };

  const handleAddNew = () => {
    const email = searchTerm.trim();
    if (email && email.includes('@') && !existingEmails.includes(email)) {
      setSearchTerm('');
      setResults([]);
      setShowDropdown(false);
      onAddNew(email);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown && e.key === 'Enter' && searchTerm.trim()) {
      handleAddNew();
      return;
    }

    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => 
        prev < results.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      } else if (searchTerm.trim() && searchTerm.includes('@')) {
        handleAddNew();
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const isValidEmail = (email) => {
    return email.includes('@') && email.length > 3;
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <div className="dropdown-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="dropdown-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
        />
        {loading && <div className="dropdown-spinner">⏳</div>}
      </div>
      
      {showDropdown && (
        <div className="dropdown-menu">
          {results.length > 0 ? (
            <>
              {results.map((user, index) => (
                <div
                  key={user.id}
                  className={`dropdown-item ${selectedIndex === index ? 'selected' : ''}`}
                  onClick={() => handleSelect(user)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="dropdown-item-name">{user.name}</div>
                  <div className="dropdown-item-email">{user.email}</div>
                </div>
              ))}
              {isValidEmail(searchTerm) && !results.find(r => r.email.toLowerCase() === searchTerm.toLowerCase()) && (
                <div
                  className="dropdown-item add-new-item"
                  onClick={handleAddNew}
                >
                  <span className="add-icon">+</span>
                  <span>Add "{searchTerm}" to organization</span>
                </div>
              )}
            </>
          ) : searchTerm && isValidEmail(searchTerm) ? (
            <div className="dropdown-item add-new-item" onClick={handleAddNew}>
              <span className="add-icon">+</span>
              <span>Add "{searchTerm}" to organization</span>
            </div>
          ) : (
            <div className="dropdown-item no-results">
              {searchTerm.length < 2 
                ? 'Type at least 2 characters to search'
                : 'No users found. Type a complete authenticated email to add it'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableEmailDropdown;
